define(function (require, exports, module) {
    'use strict';

    var config = require('Cloud/config');
    var features = require('Cloud/Application/Features');
    var uploadRadar = require('./uploadRadar');
    var AsyncIterator = require('./tools/async/AsyncIterator');
    var UploadFail = require('./fails/UploadFail');
    var UploadError = require('./errors/UploadError');
    var UploadLogRecord = require('./UploadLogRecord');
    const throttle = require('lodash.throttle');
    const { sendGaUploader } = require('reactApp/modules/uploadList/uploadList.helpers');

    /**
     * @namespace
     */
    var uploadLog = {
        /**
         * Максимальное количество записей в логе.
         * @type {number}
         */
        RECORDS_LIMIT: features.getFeatureParam('newUpload', 'maxLogRecordsCount', 10000),

        /**
         * При превышении лимита на количество записей,
         * они не начинают сразу удаляться по  одной штуке из начала списка,
         * а дожидаются превышения указанного порога, после которого
         * записи удалеяются сразу большой порцией.
         * @type {number}
         */
        RECORDS_THRESHOLD: features.getFeatureParam('newUpload', 'logRecordsThreshold', 1000),

        /**
         * Максимальное количество записей в одном запросе.
         * @type {number}
         */
        RECORDS_GROUP_SIZE: features.getFeatureParam('newUpload', 'logRecordsGroupSize', 50),

        /**
         * Время задерки между попытками отправки записей (в миллисекундах).
         * @type {number}
         */
        SEND_DELAY_TIME: features.getFeatureParam('newUpload', 'logSendDelay', 3000),

        /**
         * Разрешает отправку rlog_message.
         * @type {boolean}
         */
        IS_SENDING_ALLOWED: features.getFeatureParam('newUpload', 'kaktam', false),

        /**
         * Уровень логирования:
         *     • 0 – только ошибки
         *     • 1 – ошибки и фейлы
         *     • 2 – все записи
         * @type {number}
         */
        LOG_LEVEL: features.getFeatureParam('newUpload', 'kaktamLevel', 1),

        /**
         * Автоматическое отображение записей в консоли браузера.
         * @type {boolean}
         */
        debug: config.get('DEV_SERVER'),

        /**
         * @type {Array}
         */
        records: [],

        clear: function () {
            uploadLog.records.length = 0;
        },

        _sortRecords: function () {
            var records = uploadLog.records;

            records.sort(function sortFunction(a, b) {
                if (a.timeStamp < b.timeStamp) {
                    return -1;
                } else if (a.timeStamp > b.timeStamp) {
                    return 1;
                } else {
                    return 0;
                }
            });
        },

        /**
         * @param {string} type – info, fail or error
         * @param {Object} data
         * @param {number} [level] – overrides log level for important data
         */
        log: function (type, data, level) {
            var records = uploadLog.records;
            var record = new UploadLogRecord(type, data, level);
            var recordsCount = records.push(record);

            record.sendCounter();

            if (type === 'fail' || type === 'error') {
                sendGaUploader(data.radarName);
            }

            if (uploadLog.debug) {
                var message = record.toString();

                switch (type) {
                    case UploadLogRecord.TYPE_ERROR:
                        console.error(message);

                        break;

                    case UploadLogRecord.TYPE_FAIL:
                        console.warn(message);

                        break;

                    case UploadLogRecord.TYPE_INFO:
                        console.log(message);
                }
            }

            var limit = uploadLog.RECORDS_LIMIT;
            var threshold = uploadLog.RECORDS_THRESHOLD;

            if (recordsCount > limit && recordsCount % threshold === 0) {
                // Удаляем часть записей из начала списка,
                // когда лимит превышен на значение threshold.
                records.splice(0, threshold);
            }

            uploadLog._sortRecords();
        },

        /**
         * @param {Object} data
         * @param {number} [level] – overrides log level for important data
         */
        info: function (data, level) {
            uploadLog.log('info', data, level);
        },

        /**
         * @param {Object} data
         * @param {number} [level] – overrides log level for important data
         */
        fail: function (data, level) {
            uploadLog.log('fail', data, level);
        },

        /**
         * @param {Object} data
         * @param {number} [level] – overrides log level for important data
         */
        error: function (data, level) {
            uploadLog.log('error', data, level);
        },

        /**
         * @param {UploadReason} reason
         */
        reason: function (reason) {
            if (reason instanceof UploadError) {
                uploadLog.error(reason);
            } else if (reason instanceof UploadFail) {
                uploadLog.fail(reason);
            } else {
                uploadLog.info(reason);
            }
        },

        /**
         * @param {UploadReason} reason
         * @param {string} customType - string to reason customize
         */
        customizedReason: function (reason, customType) {
            var radarName = customType + '_' + reason.radarName;

            uploadRadar.sendCounter(radarName);

            reason = Object.create(reason); // TODO: new UploadRetry
            reason.className +=
                customType.substr(0, 1).toUpperCase() +
                customType.substr(1).replace(/(-.)/g, function (a) {
                    return a.replace('-', '').toUpperCase();
                });
            reason.radarName = ''; // prevent radar sent
            uploadLog.reason(reason);
        },

        /**
         * Формирует порции неотплавленных записей.
         * @returns {Array.<Array>}
         */
        _getUnsentRecordGroups: function () {
            var GROUP_SIZE = uploadLog.RECORDS_GROUP_SIZE;
            var LOG_LEVEL = uploadLog.LOG_LEVEL;
            var records = uploadLog.records;
            var groups = [];
            var group = [];

            for (var i = 0, count = records.length; i < count; i++) {
                var record = records[i];

                if (record.isUnsent && record.level <= LOG_LEVEL) {
                    record.isUnsent = false;

                    var groupSize = group.push(record);

                    if (groupSize % GROUP_SIZE === 0) {
                        groups.push(group);
                        group = [];
                    }
                }
            }

            if (group.length) {
                groups.push(group);
            }

            return groups;
        },

        /**
         * @param {UploadLogRecord} record
         * @returns {Object}
         */
        _getRecordDetails: function (record) {
            var details = record.getDetails();

            return details;
        },

        /**
         * Отправляет порцию записей.
         * @param {Array.<UploadLogRecord>} records
         * @param {number} index
         * @param {Array.<Array>} groups
         * @param {Function} next
         */
        _sendRecordsGroup: function (records, index, groups, next) {
            var data = records.map(uploadLog._getRecordDetails);
            uploadRadar.sendLog(data);

            next();
        },

        /**
         * Отправляет записи небольшими порциями.
         * @returns {Promise}
         * @resolves {undefined}
         * @rejects {Error}
         */
        sendImmediate: function () {
            var promise;

            if (uploadLog.IS_SENDING_ALLOWED) {
                var recordGropus = uploadLog._getUnsentRecordGroups();

                promise = new AsyncIterator(recordGropus, uploadLog._sendRecordsGroup);
            } else {
                promise = Promise.reject(new Error('not allowed'));
            }

            return promise;
        },

        /**
         * Отправляет записи небольшими порциями.
         * @type {Function}
         */
        send: null,

        toString: function () {
            var message = uploadLog.records.reduce(function (message, record) {
                return message + '\n' + record.toString();
            }, '');

            return message;
        },

        /**
         * Отображает лог в консоли.
         */
        print: function () {
            var message = uploadLog.toString();

            console.log(message);
        },

        /**
         * Отображает лог в новом окне.
         */
        open: function () {
            var message = uploadLog.toString();
            var blob = new Blob(['<pre>', message, '</pre>'], { type: 'text/html' });
            var url = URL.createObjectURL(blob);
            var name = Date.now().toString(36);

            window.open(url, name);
        },
    };

    uploadLog.send = throttle(uploadLog.sendImmediate, uploadLog.SEND_DELAY_TIME);

    module.exports = uploadLog;
});
